import style from "@/styles/pages/Profile/CreateProfile.module.css";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { setEmail, setPassword } from "@/slices/credentialSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/dtm";
import { useNavigate } from "react-router-dom";
import { generateSessionId, setClickInsightFormErrors, setSingleClickInsightEvent } from "@/lib/util";
import { setAlloySessionId } from "@/slices/applicantSlice";
import SlideTransition from "@/styles/animation/SlideTransition";

export default function CreateProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [passwordShown, setPasswordShown] = useState(false);
  const [lcCriteriaMatch, setLcCriteriaMatch] = useState(false);
  const [ucCriteriaMatch, setUcCriteriaMatch] = useState(false);
  const [numCriteriaMatch, setNumCriteriaMatch] = useState(false);
  const [lenCriteriaMatch, setLenCriteriaMatch] = useState(false);
  const [scCriteriaMatch, setScCriteriaMatch] = useState(false);

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  };
  const credentials = useSelector((state: RootState) => state.credential);
  const application = useSelector((state: RootState) => state.application.application);
  
  const methods = useForm({
    defaultValues: {
      email: credentials.credential.email,
      password: "",
      meridianCommunication: false,
    },
  });

  useEffect(() => {
    document.title = "Create your profile";
    dispatch(setAlloySessionId(generateSessionId(true)));
    setSingleClickInsightEvent("signUp");

  }, []);

  const onError = (errors: object) => {
    setClickInsightFormErrors(errors);
  }

  const onSubmit = (data: any) => {
    dispatch(setEmail(data.email));
    dispatch(setPassword(data.password));
    navigate("/joint/phone-verification");
  };

  const handleChange = (e: { target: { value: string } }) => {
    setLcCriteriaMatch(/[a-z]/.test(e.target.value));
    setUcCriteriaMatch(/[A-Z]/.test(e.target.value));
    setNumCriteriaMatch(/\d/.test(e.target.value));
    setLenCriteriaMatch(/^.{8,12}$/gm.test(e.target.value));

    const specialCharacters = /[/<>,]/.test(e.target.value);
    setScCriteriaMatch(
      specialCharacters || e.target.value == "" ? false : true
    );
  };

  return (
    <SlideTransition>
      <main aria-labelledby="main-content" className="main-content">
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div className={style.cpHeading}>
            <h1>Create your profile</h1>
            <p className="sub-heading">
              Your email and password will give you access to online and mobile 
              banking. We will also send you updates (changes to interest rates 
              and fees, for example) via email.
            </p>
            <div className="flex flex-row mt-5">
              <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                <div className="col-span-1">
                  <label htmlFor="email">Email Address</label>
                  <div className="flex relative">
                    <span>
                      <img
                        src="assets/error-icon.svg"
                        className={clsx(
                          methods.formState?.errors?.email ? "" : "hidden",
                          "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                        )}
                        alt="error"
                        height={5}
                        width={5}
                      />
                    </span>
                    <input
                      className={clsx(
                        methods.formState?.errors?.email?.message ? "error" : "",
                        "mt-2"
                      )}
                      type="email"
                      id="email"
                      autoComplete="email"
                      maxLength={40}
                      {...methods.register("email", {
                        required: "Enter your email address",
                        pattern: {
                          value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                          message: "Email address must be a valid address",
                        },
                      })}
                    />
                  </div>

                  <small
                    className={clsx(
                      methods.formState?.errors?.email ||
                        methods.formState?.errors?.password
                        ? "hidden"
                        : "",
                      "brand-slate"
                    )}
                  >
                    youremail@domain.com
                  </small>

                  {methods.formState?.errors?.email?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                      {methods.formState.errors.email.message}
                    </span>
                  )}
                  <div className="mt-3 pass-wrapper">
                    <label htmlFor="password">Password</label>
                    <div className="flex relative">
                      <span className={clsx(passwordShown ? "hidden" : " ")}>
                        <img
                          onClick={togglePasswordVisiblity}
                          src="assets/eye-icon.svg"
                          className={clsx(
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <span className={clsx(passwordShown ? " " : "hidden")}>
                        <img
                          onClick={togglePasswordVisiblity}
                          src="assets/eye-slash-icon.svg"
                          className={clsx(
                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                          )}
                          alt="error"
                          height={5}
                          width={5}
                        />
                      </span>
                      <input
                        className={clsx(
                          methods.formState?.errors?.password?.message
                            ? "error"
                            : "",
                          "mt-2"
                        )}
                        type={passwordShown ? "text" : "password"}
                        id="password"
                        autoComplete="new-password"
                        {...methods.register("password", {
                          onChange: (e) => {
                            handleChange(e);
                          },
                          required: "Enter a password",
                          minLength: {
                            value: 8,
                            message: "Password must have at least 8 characters",
                          },
                          maxLength: {
                            value: 12,
                            message:
                              "Password must have no more than 12 characters",
                          },
                          pattern: {
                            value:
                              /((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*[//<>,]).{8,12})+$/,
                            message: "Password must match criteria",
                          },
                        })}
                      />
                    </div>
                    {methods.formState?.errors?.password?.message && (
                      <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.password.message}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 mt-5">
                    <div className="mr-5 flex items-center mt-3">
                      <span className={clsx(lcCriteriaMatch ? "hidden" : "")}>
                        <img
                          src="assets/task-unfinished-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <span className={clsx(lcCriteriaMatch ? "" : "hidden")}>
                        <img
                          src="assets/success-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>

                      <p className="ml-2 password-criteria">
                        Include a lower case letter
                      </p>
                    </div>
                    <div className="flex items-center mt-2">
                      <span className={clsx(scCriteriaMatch ? "hidden" : "")}>
                        <img
                          src="assets/task-unfinished-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <span className={clsx(scCriteriaMatch ? "" : "hidden")}>
                        <img
                          src="assets/success-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <p className="ml-2 password-criteria">
                        <span>Do not use these symbols: &lt; &gt; , /</span>
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="mr-5 flex items-center mt-2">
                      <span className={clsx(ucCriteriaMatch ? "hidden" : "")}>
                        <img
                          src="assets/task-unfinished-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <span className={clsx(ucCriteriaMatch ? "" : "hidden")}>
                        <img
                          src="assets/success-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <p className="ml-2 password-criteria">
                        Include an upper case letter
                      </p>
                    </div>
                    <div className="flex items-center mt-2">
                      <span className={clsx(lenCriteriaMatch ? "hidden" : "")}>
                        <img
                          src="assets/task-unfinished-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <span className={clsx(lenCriteriaMatch ? "" : "hidden")}>
                        <img
                          src="assets/success-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <p className="ml-2 password-criteria">
                        Must be 8 to 12 characters long
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="mr-5 flex items-center mt-2">
                      <span className={clsx(numCriteriaMatch ? "hidden" : "")}>
                        <img
                          src="assets/task-unfinished-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <span className={clsx(numCriteriaMatch ? "" : "hidden")}>
                        <img
                          src="assets/success-icon.svg"
                          alt="password hidden"
                          width={18}
                          height={12}
                        />
                      </span>
                      <p className="ml-2 password-criteria">Include a number</p>
                    </div>
                  </div>
                </div>
                <div className="mb-5">
                  <button
                    type="submit"
                    className="primary mt-5 w-full sm:w-28 h-11 px-6"
                  >
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </SlideTransition>
  );
}